import { BASE_API_URL } from '@api/index';
import { createReportingThread } from '@api/reportingAgent';
import {
    ReportingAgentEventSchema,
    type ReportingAgentEvent,
    type ToolCall,
} from '@diagon-alley/common';
import { extractApiErrorMessage } from '@utils/api-error';
import { useCallback, useRef, useState } from 'react';

/**
 * One unit of "the agent is doing something" — surfaced in the chat as
 * a progress line while the report is being built. Today every step is
 * a tool call (find-template, find-metric, extract-params, execute-
 * report, render tools); the discriminated union leaves room to add
 * non-tool phases later if useful.
 */
export interface AgentStep {
    /** Stable id so React can key without re-renders churning. */
    id: string;
    /** Raw tool name from Mastra (e.g. "find-template", "execute-report"). */
    toolName: string;
    /** User-facing label shown in the chat. Mapped from toolName. */
    label: string;
    /** Finished on arrival: a lookup the agent made, reported after the fact. */
    done?: boolean;
}

export type ReportMessage =
    | { id: string; role: 'user'; text: string; /** Set when the turn re-ran a saved report. */ savedReport?: { id: string; name: string } }
    | {
          id: string;
          role: 'assistant';
          text: string;
          isStreaming: boolean;
          steps: AgentStep[];
          /** When the turn started, for the elapsed counter on the status line. */
          startedAt: number;
          /** When the stream ended; the trail shows the final time. */
          endedAt?: number;
          /** Short lines the agent wrote between steps, in order; shown while it works and folded into the trail after. */
          narration: string[];
          /** What went wrong on this turn, shown under it rather than in a banner at the bottom of the thread. */
          error?: string;
          /** The person pressed Stop; not a failure. */
          stopped?: boolean;
      }
    | { id: string; role: 'tool'; toolCall: ToolCall };

export interface SubmitToolResponseArgs {
    toolCallId: string;
    response: Record<string, unknown>;
    /** Human-readable label shown in the chat ("You selected: Blinkit"). */
    displayLabel: string;
}

export interface UseReportingThreadResult {
    threadId: string | null;
    messages: ReportMessage[];
    isStreaming: boolean;
    /** Errors that belong to no turn (the thread could not be created). A turn's own error is on its assistant message. */
    error: string | null;
    sendMessage: (message: string) => Promise<void>;
    /** Abandons the turn in progress. The server stops the agent when the connection drops. */
    stop: () => void;
    submitToolResponse: (args: SubmitToolResponseArgs) => Promise<void>;
    /**
     * Re-runs a saved report and streams its result events into the
     * current chat thread. Uses POST /saved-reports/:id/run server-side
     * — bypasses the LLM entirely.
     */
    runSavedReport: (args: {
        savedReportId: string;
        displayLabel: string;
    }) => Promise<void>;
}

/**
 * Manages a single Reports chat thread:
 *   1. Creates the thread on first send.
 *   2. POSTs user messages OR structured tool responses to the matching
 *      SSE endpoint.
 *   3. Parses typed ReportingAgentEvent chunks (validated against the
 *      shared Zod schema) and updates message state in place.
 *
 * SSE consumption uses fetch + ReadableStream (NOT EventSource) so the
 * Authorization bearer token can be attached per the protocol spec.
 */
export function useReportingThread(): UseReportingThreadResult {
    const [threadId, setThreadId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ReportMessage[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const threadIdRef = useRef<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const ensureThreadId = useCallback(async (): Promise<string> => {
        if (threadIdRef.current) return threadIdRef.current;
        const { threadId: newThreadId } = await createReportingThread();
        threadIdRef.current = newThreadId;
        setThreadId(newThreadId);
        return newThreadId;
    }, []);

    /**
     * Puts the question and an empty answer on screen at once, so the
     * page reacts before the thread is even created. Returns the answer
     * message's id for the stream to fill in.
     */
    const beginTurn = useCallback((userMessage: ReportMessage): string => {
        setError(null);
        const assistantId = crypto.randomUUID();
        setMessages((prev) => [
            ...prev,
            userMessage,
            {
                id: assistantId,
                role: 'assistant',
                text: '',
                isStreaming: true,
                steps: [],
                startedAt: Date.now(),
                narration: [],
            },
        ]);
        setIsStreaming(true);
        return assistantId;
    }, []);

    /** The turn could not be sent at all (no thread): say so on the turn. */
    const failTurn = useCallback((assistantId: string, err: unknown) => {
        setMessages((prev) =>
            prev.map((m) =>
                m.id === assistantId && m.role === 'assistant'
                    ? { ...m, isStreaming: false, endedAt: Date.now(), error: extractApiErrorMessage(err) }
                    : m,
            ),
        );
        setIsStreaming(false);
    }, []);

    const runStreamingTurn = useCallback(
        async (
            url: string,
            body: object,
            assistantId: string,
        ): Promise<void> => {
            const abort = new AbortController();
            abortRef.current = abort;

            try {
                const token = localStorage.getItem('token');
                const response = await fetch(url, {
                    method: 'POST',
                    signal: abort.signal,
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'text/event-stream',
                        ...(token
                            ? { Authorization: `Bearer ${token}` }
                            : {}),
                    },
                    body: JSON.stringify(body),
                });

                if (!response.ok || !response.body) {
                    // Surface Nest's JSON error body when present so users
                    // see "response must be an object" instead of a bare
                    // "HTTP 422". Falls back to the status if the body
                    // isn't JSON (proxy 502s, etc).
                    let detail = '';
                    try {
                        const text = await response.text();
                        if (text) {
                            try {
                                const parsed = JSON.parse(text) as {
                                    message?: string | string[];
                                };
                                const msg = Array.isArray(parsed.message)
                                    ? parsed.message.join('; ')
                                    : parsed.message;
                                if (msg) detail = msg;
                            } catch {
                                detail = text.slice(0, 200);
                            }
                        }
                    } catch {
                        // ignore — fall back to status only
                    }
                    throw new Error(
                        detail
                            ? `HTTP ${response.status}: ${detail}`
                            : `HTTP ${response.status}`,
                    );
                }

                await consumeSseStream(
                    response.body.getReader(),
                    assistantId,
                    setMessages,
                );
            } catch (err) {
                const stopped = abort.signal.aborted;
                setMessages((prev) =>
                    prev.map((m) =>
                        m.id === assistantId && m.role === 'assistant'
                            ? stopped
                                ? { ...m, stopped: true }
                                : { ...m, error: extractApiErrorMessage(err) }
                            : m,
                    ),
                );
            } finally {
                if (abortRef.current === abort) abortRef.current = null;
                setIsStreaming(false);
                setMessages((prev) =>
                    prev.map((m) =>
                        m.id === assistantId && m.role === 'assistant'
                            ? { ...m, isStreaming: false, endedAt: Date.now() }
                            : m,
                    ),
                );
            }
        },
        [],
    );

    // Each entry point puts the turn on screen first, then creates the
    // thread: when the backend is down, createReportingThread throws before
    // the stream starts, and the person still sees their question and an
    // error under it rather than nothing at all.
    //
    // A send already in flight: a second click during the thread round
    // trip must not open a second thread with the same question.
    const sendingRef = useRef(false);

    const sendMessage = useCallback(
        async (message: string) => {
            const trimmed = message.trim();
            if (!trimmed || sendingRef.current) return;
            sendingRef.current = true;
            const assistantId = beginTurn({ id: crypto.randomUUID(), role: 'user', text: trimmed });
            try {
                let tid: string;
                try {
                    tid = await ensureThreadId();
                } catch (err) {
                    failTurn(assistantId, err);
                    return;
                }

                const apiPrefix = BASE_API_URL
                    ? `${BASE_API_URL}/api/v1`
                    : '/api/v1';
                const url = `${apiPrefix}/reporting/threads/${tid}/messages`;

                await runStreamingTurn(url, { message: trimmed }, assistantId);
            } finally {
                sendingRef.current = false;
            }
        },
        [beginTurn, ensureThreadId, failTurn, runStreamingTurn],
    );

    const submitToolResponse = useCallback(
        async (args: SubmitToolResponseArgs) => {
            if (sendingRef.current) return;
            sendingRef.current = true;
            const assistantId = beginTurn({ id: crypto.randomUUID(), role: 'user', text: args.displayLabel });
            try {
                let tid: string;
                try {
                    tid = await ensureThreadId();
                } catch (err) {
                    failTurn(assistantId, err);
                    return;
                }

                const apiPrefix = BASE_API_URL
                    ? `${BASE_API_URL}/api/v1`
                    : '/api/v1';
                const url = `${apiPrefix}/reporting/threads/${tid}/tool-response`;

                await runStreamingTurn(
                    url,
                    { toolCallId: args.toolCallId, response: args.response },
                    assistantId,
                );
            } finally {
                sendingRef.current = false;
            }
        },
        [beginTurn, ensureThreadId, failTurn, runStreamingTurn],
    );

    const runSavedReport = useCallback(
        async (args: { savedReportId: string; displayLabel: string }) => {
            if (sendingRef.current) return;
            sendingRef.current = true;
            const assistantId = beginTurn({ id: crypto.randomUUID(), role: 'user', text: `▶ ${args.displayLabel}`, savedReport: { id: args.savedReportId, name: args.displayLabel } });
            try {
                // Saved-report re-runs don't need a Mastra thread — they
                // bypass the agent. We still ensure a threadId exists so
                // the chat thread state is consistent if the user switches
                // back to chatting after.
                try {
                    await ensureThreadId();
                } catch (err) {
                    failTurn(assistantId, err);
                    return;
                }

                const apiPrefix = BASE_API_URL
                    ? `${BASE_API_URL}/api/v1`
                    : '/api/v1';
                const url = `${apiPrefix}/saved-reports/${args.savedReportId}/run`;

                await runStreamingTurn(url, {}, assistantId);
            } finally {
                sendingRef.current = false;
            }
        },
        [beginTurn, ensureThreadId, failTurn, runStreamingTurn],
    );

    const stop = useCallback(() => {
        abortRef.current?.abort();
    }, []);

    return {
        threadId,
        messages,
        isStreaming,
        error,
        sendMessage,
        stop,
        submitToolResponse,
        runSavedReport,
    };
}

async function consumeSseStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    assistantId: string,
    setMessages: React.Dispatch<React.SetStateAction<ReportMessage[]>>,
): Promise<void> {
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6).trim();
            if (!data) continue;

            let parsed: ReportingAgentEvent;
            try {
                const json = JSON.parse(data) as unknown;
                const result = ReportingAgentEventSchema.safeParse(json);
                if (!result.success) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        '[reporting] discarded malformed event',
                        result.error.issues,
                    );
                    continue;
                }
                parsed = result.data;
            } catch {
                continue;
            }

            handleEvent(parsed, assistantId, setMessages);
        }
    }
}

function handleEvent(
    event: ReportingAgentEvent,
    assistantId: string,
    setMessages: React.Dispatch<React.SetStateAction<ReportMessage[]>>,
): void {
    if (event.type === 'text_delta') {
        setMessages((prev) =>
            prev.map((m) =>
                m.id === assistantId && m.role === 'assistant'
                    ? { ...m, text: m.text + event.text }
                    : m,
            ),
        );
        return;
    }

    if (event.type === 'tool_call_completed') {
        setMessages((prev) => [
            ...prev,
            {
                id: event.toolCallId,
                role: 'tool',
                toolCall: event.toolCall,
            },
        ]);
        return;
    }

    if (event.type === 'tool_call_started') {
        // Append a progress step on the in-flight assistant message so
        // the user sees what the agent is doing instead of a static
        // "Thinking…" placeholder. Render tools (showResultTable etc.)
        // become their own messages on tool_call_completed, so we skip
        // them here — narrate only the work-in-progress phases.
        const label = stepLabel(event.name, event.title);
        if (!label) return;
        setMessages((prev) =>
            prev.map((m) => {
                if (m.id !== assistantId || m.role !== 'assistant') return m;
                if (m.steps.some((s) => s.id === event.toolCallId)) return m;
                // Text written before another step starts is the agent
                // saying what it is about to do, not the answer. A retry
                // repeats the line; the person needs it once.
                const line = m.text.trim();
                const narration = line && line !== m.narration[m.narration.length - 1] ? [...m.narration, line] : m.narration;
                return {
                    ...m,
                    text: m.text.trim() ? '' : m.text,
                    narration,
                    steps: [
                        ...m.steps,
                        {
                            id: event.toolCallId,
                            toolName: event.name,
                            label,
                        },
                    ],
                };
            }),
        );
        return;
    }

    if (event.type === 'lookup_completed') {
        // Data the agent read without showing it. Named so the person
        // knows where a figure in the answer came from. A report's own
        // whole-result figures come through the same event and are
        // already on screen as the report, so they get no line.
        const title = event.title;
        if (!title || event.toolCallId.startsWith('figures-')) return;
        const rows =
            event.rowCount === undefined
                ? ''
                : ` · ${event.rowCount === 1 ? '1 row' : `${event.rowCount.toLocaleString()} rows`}`;
        const looked: AgentStep = {
            id: event.toolCallId,
            toolName: 'lookup',
            label: `Looked at ${title}${rows}`,
            done: true,
        };
        setMessages((prev) =>
            prev.map((m) => {
                if (m.id !== assistantId || m.role !== 'assistant') return m;
                if (m.steps.some((s) => s.id === event.toolCallId)) return m;
                // The "Looking at the data" line this result belongs to
                // becomes the result, rather than staying beside it.
                const pending = m.steps.findLastIndex(
                    (s) => s.toolName === 'query-data' && !s.done,
                );
                const steps =
                    pending >= 0
                        ? m.steps.map((s, i) => (i === pending ? looked : s))
                        : [...m.steps, looked];
                return { ...m, steps };
            }),
        );
        return;
    }

    if (event.type === 'error') {
        setMessages((prev) =>
            prev.map((m) =>
                m.id === assistantId && m.role === 'assistant'
                    ? { ...m, error: event.message }
                    : m,
            ),
        );
    }

    // tool_call_failed, done — silent for now.
}

/**
 * The status line for a step. A step that carries the agent's own title
 * for what it is reading says so; the rest fall back to the generic label.
 */
function stepLabel(name: string, title: string | undefined): string | undefined {
    if (title) {
        if (name === 'query-data') return `Reading ${lowerFirst(title)}…`;
        if (name === 'execute-freeform-report' || name === 'execute-report') return `Building ${lowerFirst(title)}…`;
    }
    return TOOL_STEP_LABELS[name];
}

/** Titles arrive capitalised as headings; mid-sentence they read better lowered, except acronyms. */
function lowerFirst(text: string): string {
    return /^[A-Z][a-z]/.test(text) ? text.charAt(0).toLowerCase() + text.slice(1) : text;
}

/**
 * Maps Mastra tool ids to user-facing progress labels. Only includes
 * the work-in-progress phases — render tools (showResultTable,
 * showResultChart, showKpiScorecard, showDownloadArtifact, etc.)
 * become their own visible message on completion, so they don't need
 * a separate narration step.
 *
 * Unknown tool ids fall through (no step rendered) — better to show
 * less than to show "Calling unknownTool" gibberish.
 */
const TOOL_STEP_LABELS: Record<string, string> = {
    'find-template': 'Searching report templates',
    'find-metric': 'Identifying the right metric',
    'extract-params': 'Validating parameters',
    'execute-report': 'Building the report…',
    'lookup-services': 'Looking up data sources',
    'describe-data': 'Working out which data to use…',
    'query-data': 'Looking at the data…',
    'execute-freeform-report': 'Building the report…',
    'show-clarification-card': 'Preparing a clarification question',
    'show-report-preview': 'Building report preview',
    'show-progress': 'Updating progress',
};
